import { useSnackbar } from 'notistack';
import { useEffect, useState, useMemo, useRef } from 'react';
import { team } from 'src/backend';

// ----------------------------------------------------------------------

export default function useTeamMembers({
  fetchOnMount = true,
  page,
  pageSize,
  filterModel,
  includeLastActivity = false,
  includeJobTitle = false,
} = {}) {
  const { enqueueSnackbar } = useSnackbar();

  const [cachedTeamMembers, setCachedTeamMembers] = useState([]);
  const [count, setCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [valid, setValid] = useState(!fetchOnMount);
  const requestIdRef = useRef(0);

  const isPaginated = page !== undefined && pageSize !== undefined;

  useEffect(() => {
    if (isPaginated) {
      setValid(false);
    }
  }, [
    page,
    pageSize,
    filterModel,
    isPaginated,
    includeLastActivity,
    includeJobTitle,
  ]);

  useEffect(() => {
    if (valid) return;

    setIsLoading(true);
    const currentRequestId = ++requestIdRef.current;

    const handleError = () => {
      enqueueSnackbar(
        'An error occurred while trying to fetch your team members. Please try again later.',
        { variant: 'error' }
      );
    };

    if (isPaginated) {
      team
        .getPaginated({
          offset: page,
          step: pageSize,
          filterModel,
          includeLastActivity,
          includeJobTitle,
        })
        .then(({ results, count }) => {
          if (currentRequestId === requestIdRef.current) {
            setValid(true);
            setCachedTeamMembers(results);
            setCount(count);
          }
        })
        .catch(handleError)
        .finally(() => {
          if (currentRequestId === requestIdRef.current) {
            setIsLoading(false);
          }
        });
    } else {
      team
        .get()
        .then((result) => {
          if (currentRequestId === requestIdRef.current) {
            setValid(true);
            const members = Array.isArray(result) ? result : [];
            setCachedTeamMembers(members);
            setCount(members.length);
          }
        })
        .catch(handleError)
        .finally(() => {
          if (currentRequestId === requestIdRef.current) {
            setIsLoading(false);
          }
        });
    }
  }, [
    valid,
    isPaginated,
    page,
    pageSize,
    filterModel,
    includeLastActivity,
    includeJobTitle,
    enqueueSnackbar,
  ]);

  const activeMembers = useMemo(
    () => cachedTeamMembers?.filter((m) => m.is_active) ?? [],
    [cachedTeamMembers]
  );

  return {
    teamMembers: cachedTeamMembers,
    count,
    isLoading,
    invalidate: () => setValid(false),
    activeMembers,
  };
}
